import { isUuidv7 } from '../../core/id';
import { GAME_MODES, type GameMode } from '../../schemas/game';
import { RULE_PRESETS } from '../../schemas/rules';
import type { GameSetupPrefill, PrefillEntry, RulesPresetKey, TournamentPrefill } from './types';

/**
 * The link shapes that hand play from one screen to another (CONTRACTS "Opening a screen with
 * data"). Games exports `newGameLink`/`parseGameLink` from its public.ts, tournaments the two
 * tournament ones; both just re-export these. Ids that are not uuidv7 are dropped on parse.
 *
 *   /play/new?mode=2v2&players=a,b,c,d&teamA=a,b&teamB=c,d&rules=to21&session=<id>&tournament=<id>&fixture=<id>&drill=<id>
 *   /tournaments/new?format=round_robin&mode=1v1&entries=a,b,c&rules=quick11&session=<id>
 *
 * In `entries`, commas separate entries and dots separate the players of one team entry
 * (`entries=a.b,c.d`). `session=none` means detached (sessionId null).
 */

export const GAME_NEW_PATH = '/play/new';
export const TOURNAMENT_NEW_PATH = '/tournaments/new';

/** A search string (`?a=1`), a whole link (`/play/new?a=1`) or anything with `get`, like URLSearchParams. */
type Search = string | { get(name: string): string | null };

/** shared/ has no DOM types, so URLSearchParams is not available here. */
function reader(s: Search): { get(name: string): string | null } {
  if (typeof s !== 'string') return s;
  const map = new Map<string, string>();
  for (const part of s.slice(s.includes('?') ? s.indexOf('?') + 1 : 0).split('&')) {
    if (!part) continue;
    const eq = part.indexOf('=');
    const k = decodeURIComponent((eq < 0 ? part : part.slice(0, eq)).replace(/\+/g, ' '));
    const v = eq < 0 ? '' : decodeURIComponent(part.slice(eq + 1).replace(/\+/g, ' '));
    if (!map.has(k)) map.set(k, v);
  }
  return { get: (n) => map.get(n) ?? null };
}

function build(pairs: [string, string][]): string {
  return pairs.map(([k, v]) => `${k}=${encodeURIComponent(v).replace(/%2C/gi, ',').replace(/%2E/g, '.')}`).join('&');
}
const ids = (v: string | null, sep = ','): string[] => (v ? v.split(sep).filter((x) => isUuidv7(x)) : []);
const mode = (v: string | null): GameMode | undefined =>
  (GAME_MODES as readonly string[]).includes(v ?? '') ? (v as GameMode) : undefined;
const preset = (v: string | null): RulesPresetKey | undefined =>
  v && v in RULE_PRESETS ? (v as RulesPresetKey) : undefined;
const session = (v: string | null): string | null | undefined =>
  v === 'none' ? null : v && isUuidv7(v) ? v : undefined;

export function newGameLink(p: GameSetupPrefill): string {
  const q: [string, string][] = [];
  if (p.mode) q.push(['mode', p.mode]);
  if (p.playerIds?.length) q.push(['players', p.playerIds.join(',')]);
  if (p.teamA?.length) q.push(['teamA', p.teamA.join(',')]);
  if (p.teamB?.length) q.push(['teamB', p.teamB.join(',')]);
  if (p.rulesPreset) q.push(['rules', p.rulesPreset]);
  if (p.sessionId === null) q.push(['session', 'none']);
  else if (p.sessionId) q.push(['session', p.sessionId]);
  if (p.tournamentId) q.push(['tournament', p.tournamentId]);
  if (p.fixtureId) q.push(['fixture', p.fixtureId]);
  if (p.drillSessionId) q.push(['drill', p.drillSessionId]);
  return q.length ? `${GAME_NEW_PATH}?${build(q)}` : GAME_NEW_PATH;
}

export function parseGameLink(search: Search): GameSetupPrefill {
  const q = reader(search);
  const out: GameSetupPrefill = {};
  const m = mode(q.get('mode'));
  if (m) out.mode = m;
  const players = ids(q.get('players'));
  if (players.length) out.playerIds = players;
  const a = ids(q.get('teamA'));
  const b = ids(q.get('teamB'));
  if (a.length) out.teamA = a;
  if (b.length) out.teamB = b;
  const r = preset(q.get('rules'));
  if (r) out.rulesPreset = r;
  const s = session(q.get('session'));
  if (s !== undefined) out.sessionId = s;
  const t = q.get('tournament');
  if (t && isUuidv7(t)) out.tournamentId = t;
  const f = q.get('fixture');
  if (f && isUuidv7(f)) out.fixtureId = f;
  const d = q.get('drill');
  if (d && isUuidv7(d)) out.drillSessionId = d;
  return out;
}

export function newTournamentLink(p: TournamentPrefill): string {
  const q: [string, string][] = [
    ['format', p.format],
    ['mode', p.mode],
    ['entries', p.entries.map((e) => e.playerIds.join('.')).join(',')],
  ];
  if (p.rulesPreset) q.push(['rules', p.rulesPreset]);
  if (p.sessionId === null) q.push(['session', 'none']);
  else if (p.sessionId) q.push(['session', p.sessionId]);
  return `${TOURNAMENT_NEW_PATH}?${build(q)}`;
}

/** null when the link has no usable format, mode or entries. */
export function parseTournamentLink(search: Search): TournamentPrefill | null {
  const q = reader(search);
  const format = q.get('format');
  const m = mode(q.get('mode'));
  if ((format !== 'round_robin' && format !== 'single_elim') || !m) return null;
  const entries: PrefillEntry[] = (q.get('entries') ?? '')
    .split(',')
    .map((e) => ({ playerIds: ids(e, '.') }))
    .filter((e) => e.playerIds.length > 0);
  if (entries.length < 2) return null;
  const out: TournamentPrefill = { format, mode: m, entries };
  const r = preset(q.get('rules'));
  if (r) out.rulesPreset = r;
  const s = session(q.get('session'));
  if (s !== undefined) out.sessionId = s;
  return out;
}
